/**
 * @file pages/ChatPage.tsx
 * Page = RAG (servie sous /rag), PAS le chat agent conversationnel.
 * Le chemin /chat est réservé au futur front ConversationEngine (P1).
 *
 * Espace de travail "WhatsApp-style" (page d'accueil PWA).
 *
 * L'utilisateur dialogue avec la plateforme d'agents : il dépose un document,
 * suit le traitement en temps réel (SSE), reçoit une carte de résultat enrichie,
 * valide les étapes HITL inline et pose des questions en langage naturel (RAG).
 *
 * Réutilise : AuthContext (token), SDKContext (SDK + simulation), usePWA.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutDashboard, WifiOff, ArrowDown, Volume2, VolumeX, Menu } from 'lucide-react';

import { useSDK } from '../context/SDKContext.tsx';
import { useAuth } from '../context/AuthContext.tsx';
import { usePWA } from '../hooks/usePWA.ts';
import { useChatThread } from '../hooks/useChatThread.ts';
import { useTextToSpeech } from '../hooks/useTextToSpeech.ts';
import { useChatConfig } from '../hooks/useChatConfig.ts';
import { DataClassificationBadge } from '../components/chat/DataClassificationBadge.tsx';

import MessageBubble from '../components/chat/MessageBubble.tsx';
import DocumentMessage from '../components/chat/DocumentMessage.tsx';
import RichResultCard from '../components/chat/RichResultCard.tsx';
import HitlActionCard from '../components/chat/HitlActionCard.tsx';
import TypingIndicator from '../components/chat/TypingIndicator.tsx';
import AgentStatusBubble from '../components/chat/AgentStatusBubble.tsx';
import ChatComposer from '../components/chat/ChatComposer.tsx';
import DaySeparator from '../components/chat/DaySeparator.tsx';
import ConversationList, { useConversations } from '../components/chat/ConversationList.tsx';
import type { ChatMessage } from '../components/chat/types.ts';
import AiNotice from '../components/AiNotice.tsx';
import { WelcomeScreen } from '../components/chat/WelcomeScreen.tsx';

// ── 7.3-03 : séparateurs de date & regroupement des bulles ──────────────────

/** Fenêtre de regroupement : bulles d'un même auteur espacées de < 2 min. */
const GROUP_WINDOW_MS = 2 * 60 * 1000;

function sameDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

/** Deux messages texte consécutifs du même auteur, rapprochés et le même jour. */
function isGrouped(a: ChatMessage | undefined, b: ChatMessage | undefined): boolean {
  if (!a || !b) return false;
  if (a.kind !== 'text' || b.kind !== 'text') return false;
  return a.author === b.author && b.at - a.at < GROUP_WINDOW_MS && sameDay(a.at, b.at);
}

export default function ChatPage(): React.ReactElement {
  const { sdk, isSimulated } = useSDK();
  const { logout } = useAuth();
  const { isOnline, installable, triggerInstall } = usePWA();
  const { config } = useChatConfig();

  // ── 7.3-06 : multi-conversations ───────────────────────────────────────────
  // flushRef casse la dépendance circulaire conv ↔ thread : la liste sauvegarde
  // le fil courant juste avant de basculer de conversation.
  const flushRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const conv = useConversations(() => flushRef.current());
  const thread = useChatThread(conv.loading ? null : conv.activeId);
  flushRef.current = thread.flush;
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Rafraîchit les aperçus de la liste après la persistance du fil (~300 ms).
  const refreshList = conv.refresh;
  useEffect(() => {
    const t = setTimeout(refreshList, 600);
    return () => clearTimeout(t);
  }, [thread.messages, refreshList]);

  // 7.3-08 : réglage global lecture à voix haute (persisté).
  const tts = useTextToSpeech('fr-FR');

  const scrollRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  // ── 7.3-04 : auto-scroll conditionnel + pastille "nouveau message" ─────────
  // On ne défile automatiquement que si l'utilisateur est déjà près du bas
  // (seuil ~120px). Sinon, un bouton flottant avec compteur lui permet de
  // revenir en bas sans être interrompu dans sa lecture de l'historique.
  const NEAR_BOTTOM_PX = 120;
  const nearBottomRef = useRef(true);
  const prevCountRef = useRef(0);
  const [newCount, setNewCount] = useState(0);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = scrollRef.current;
    el?.scrollTo({ top: el.scrollHeight, behavior });
    setNewCount(0);
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    nearBottomRef.current = nearBottom;
    if (nearBottom) setNewCount(0);
  }, []);

  useEffect(() => {
    const added = thread.messages.length - prevCountRef.current;
    prevCountRef.current = thread.messages.length;
    if (added <= 0) return;
    const last = thread.messages[thread.messages.length - 1];
    // Envoyer soi-même un message défile toujours en bas (réflexe messagerie).
    if (last?.author === 'user' || nearBottomRef.current) {
      scrollToBottom();
    } else {
      setNewCount((c) => c + added);
    }
  }, [thread.messages, scrollToBottom]);

  // Le typing indicator ne force le scroll que si on est déjà en bas.
  useEffect(() => {
    if (thread.agentBusy && nearBottomRef.current) scrollToBottom();
  }, [thread.agentBusy, scrollToBottom]);

  // ── Drag & drop de fichier sur le fil ──────────────────────────────────────
  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) thread.attachFile(file);
    },
    [thread],
  );

  // ── Actions des cartes de résultat ─────────────────────────────────────────
  const handleDownloadReport = useCallback(
    (dossierId: string) => {
      sdk.sessions.getResult(dossierId).then(({ data }) => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rapport-${dossierId}.json`;
        a.click();
        URL.revokeObjectURL(url);
      });
    },
    [sdk],
  );

  const handleCheckCompliance = useCallback(
    (_dossierId: string) => {
      thread.sendText('Vérifie la conformité des clauses de ce document.');
    },
    [thread],
  );

  // 7.3-02 : réessayer — bulle utilisateur → ré-émet ce message ;
  // bulle agent en erreur (info/security) → relance la dernière requête.
  const handleRetry = useCallback(
    (m: { id: string; author: 'user' | 'agent' }) => {
      if (m.author === 'user') {
        thread.resendMessage(m.id);
      } else {
        thread.retryLast();
      }
    },
    [thread],
  );

  return (
    <div className="flex h-[100dvh] bg-background">
      {/* 7.3-06 : liste de conversations — colonne fixe en desktop */}
      <aside className="hidden w-72 shrink-0 border-r border-border md:block">
        <ConversationList
          conversations={conv.conversations}
          activeId={conv.activeId}
          onSelect={conv.select}
          onCreate={conv.create}
          onRename={conv.rename}
          onDelete={conv.remove}
        />
      </aside>

      {/* 7.3-06 : drawer coulissant en mobile */}
      {drawerOpen && (
        <div className="fixed inset-0 z-30 md:hidden">
          <div
            aria-hidden
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-72 border-r border-border bg-card shadow-xl">
            <ConversationList
              conversations={conv.conversations}
              activeId={conv.activeId}
              onSelect={(id) => {
                conv.select(id);
                setDrawerOpen(false);
              }}
              onCreate={() => {
                conv.create();
                setDrawerOpen(false);
              }}
              onRename={conv.rename}
              onDelete={conv.remove}
            />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
      {/* En-tête type messagerie */}
      <header className="flex items-center justify-between border-b border-border bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Ouvrir la liste des conversations"
            onClick={() => setDrawerOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground md:hidden"
          >
            <Menu size={18} />
          </button>
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground text-base">
            {config?.avatar_emoji}
          </div>
          <div className="leading-tight">
            <p className="text-sm font-semibold text-foreground flex items-center gap-2">
              <span>{config?.agent_name ?? 'Assistia'}</span>
              <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-medium border ${
                thread.activeDossierId
                  ? 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                  : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
              }`}>
                {thread.activeDossierId ? 'Mode Dossier' : 'Mode Conversation'}
              </span>
              {config?.data_classification && (
                <DataClassificationBadge
                  classification={config.data_classification as 'C1' | 'C2' | 'C3' | 'C4'}
                />
              )}
            </p>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
              <span>{isSimulated ? 'Mode démonstration' : thread.agentBusy ? 'en train d’écrire…' : 'en ligne'}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {!isOnline && (
            <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-[11px] text-muted-foreground">
              <WifiOff size={12} /> Hors-ligne
            </span>
          )}
          {installable && (
            <button
              type="button"
              onClick={triggerInstall}
              className="rounded-md border border-border px-2.5 py-1 text-[11px] font-medium text-foreground hover:bg-accent"
            >
              Installer l’app
            </button>
          )}
          {tts.supported && (
            <button
              type="button"
              onClick={() => tts.setEnabled(!tts.enabled)}
              title={tts.enabled ? 'Désactiver la lecture à voix haute' : 'Activer la lecture à voix haute'}
              aria-pressed={tts.enabled}
              className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            >
              {tts.enabled ? <Volume2 size={17} /> : <VolumeX size={17} />}
            </button>
          )}
          <Link
            to="/dashboard"
            title="Console d'administration"
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <LayoutDashboard size={17} />
          </Link>
          <button
            type="button"
            onClick={logout}
            className="rounded-md px-2.5 py-1 text-[11px] font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            Déconnexion
          </button>
        </div>
      </header>

      <div className="px-4 pt-3">
        <AiNotice
          variant="banner"
          text={thread.activeDossierId ? config.disclaimer_rag : config.disclaimer_cowork}
        />
      </div>

      {/* Fil de discussion */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative flex-1 overflow-hidden ${
          dragging ? 'ring-2 ring-inset ring-primary' : ''
        }`}
      >
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="h-full overflow-y-auto px-4 py-4"
        >
        {thread.messages.length === 0 && (
          <WelcomeScreen config={config} />
        )}

        {thread.messages.map((m, i) => {
          const prev = thread.messages[i - 1];
          const next = thread.messages[i + 1];
          // 7.3-03 : puce de date quand le jour change entre deux messages.
          const newDay = !prev || !sameDay(prev.at, m.at);
          // Regroupement : espacement réduit avec la bulle précédente du groupe ;
          // avatar + heure portés par la DERNIÈRE bulle du groupe uniquement.
          const groupedWithPrev = !newDay && isGrouped(prev, m);
          const groupedWithNext = isGrouped(m, next);

          let node: React.ReactNode;
          switch (m.kind) {
            case 'text':
              node = (
                <MessageBubble
                  message={m}
                  onRetry={handleRetry}
                  showAvatar={!groupedWithNext}
                  showMeta={!groupedWithNext}
                />
              );
              break;
            case 'document':
              node = <DocumentMessage message={m} />;
              break;
            case 'result':
              node = (
                <RichResultCard
                  message={m}
                  onDownloadReport={handleDownloadReport}
                  onCheckCompliance={handleCheckCompliance}
                />
              );
              break;
            case 'hitl':
              node = (
                <HitlActionCard
                  message={m}
                  busy={thread.hitlBusy}
                  onDecision={thread.decideHitl}
                />
              );
              break;
            default:
              node = null;
          }

          return (
            <React.Fragment key={m.id}>
              {newDay && <DaySeparator at={m.at} />}
              <div className={groupedWithPrev ? 'mt-1' : 'mt-3'}>{node}</div>
            </React.Fragment>
          );
        })}

        {/* CHAT-06b — Bulle de statut pendant le traitement agent.
            Disparaît dès le premier token (agentActivity remis à '' dans useChatThread). */}
        {thread.agentBusy && thread.agentActivity !== '' && (
          <div className="mt-3">
            <AgentStatusBubble text={thread.agentActivity} visible />
          </div>
        )}
        </div>

        {/* 7.3-04 : pastille flottante "nouveau message" */}
        {newCount > 0 && (
          <button
            type="button"
            onClick={() => scrollToBottom()}
            aria-label={`Défiler vers le bas — ${newCount} nouveau${newCount > 1 ? 'x' : ''} message${newCount > 1 ? 's' : ''}`}
            className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-lg transition-colors hover:bg-accent"
          >
            <ArrowDown size={14} />
            {newCount > 1 ? `${newCount} nouveaux messages` : 'Nouveau message'}
          </button>
        )}

        {dragging && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-primary/5">
            <span className="rounded-lg border border-dashed border-primary bg-card px-4 py-2 text-sm font-medium text-primary">
              Déposez le document ici
            </span>
          </div>
        )}
      </div>

      {/* Barre de saisie */}
      <ChatComposer
          onSendText={thread.sendText}
          onAttachFile={thread.attachFile}
          onUploadAttachment={thread.uploadAttachment}
          activeDossierId={thread.activeDossierId}
        />
      </div>
    </div>
  );
}
